// src/app/features/taxExtensions/TaxExtensionForm/FormFields.tsx
import { BusinessTypesOptions } from 'app/features/profile/businessDetails/addBusinessDetails/formFields';
import { ITaxExtension, ITaxExtensionForm } from 'app/services/taxExtensions';
import { Fragment, useEffect } from 'react';
import renderInput from 'utils/renderInput';
import { Validators } from 'utils/Validators';
import * as Yup from 'yup';

// Automatically set the tax year to the next year
const nextYear = new Date().getFullYear() - 1;

export const validationSchema = Yup.object().shape({
  // General
  year: Yup.number().nullable().required('Required'),
  type: Yup.string().nullable().required('Required'),
  business_id: Yup.string().when('type', {
    is: (v: string) => v === 'business',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  // Business Type remains validated elsewhere if needed.
  firstName: Yup.string().nullable().required('Required'),
  lastName: Yup.string().nullable().required('Required'),
  ssn: Yup.string().nullable().required('Required'),
  phoneNumber: Yup.string().nullable().required('Required'),
  email: Yup.string().nullable().required('Required'),

  // Spouse (for individuals)
  isMarried: Yup.string().when('type', {
    is: (v: string) => v === 'individual',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  spouseFirstName: Yup.string().when(['isMarried', 'type'], {
    is: (isMarried: string, type: string) =>
      isMarried === 'yes' && type === 'individual',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  spouseLastName: Yup.string().when(['isMarried', 'type'], {
    is: (isMarried: string, type: string) =>
      isMarried === 'yes' && type === 'individual',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  spouseSSN: Yup.string().when(['isMarried', 'type'], {
    is: (isMarried: string, type: string) =>
      isMarried === 'yes' && type === 'individual',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),

  // Dependents (for individuals)
  haveDependents: Yup.string().when('type', {
    is: (v: string) => v === 'individual',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  depenendentsCountUnder16: Yup.string().when(['haveDependents', 'type'], {
    is: (haveDependents: string, type: string) =>
      haveDependents === 'yes' && type === 'individual',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  otherdepenendentsCount: Yup.string().when(['haveDependents', 'type'], {
    is: (haveDependents: string, type: string) =>
      haveDependents === 'yes' && type === 'individual',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),

  // Address
  address: Yup.string().nullable().required('Required'),
  city: Yup.string().nullable().required('Required'),
  state: Yup.string().nullable().required('Required'),
  zipCode: Validators.zipCode.required('Required'),
  liveOutsideOfUs: Yup.string().nullable().required('Required'),
  otherCountry: Yup.string().when('liveOutsideOfUs', {
    is: (v: string) => v === 'yes',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  provinceCounty: Yup.string().when('liveOutsideOfUs', {
    is: (v: string) => v === 'yes',
    then: Yup.string().nullable().required('Required'),
    otherwise: Yup.string().nullable(),
  }),

  agreement: Yup.boolean().nullable().isTrue('Required'),

  // --- Individual-Specific Fields ---
  completedFederalReturn: Yup.string().when('type', {
    is: (v: string) => v === 'individual',
    then: Yup.string().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  filingStatus: Yup.string().when('type', {
    is: (v: string) => v === 'individual',
    then: Yup.string().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  priorYearAGI: Yup.number().when('type', {
    is: (v: string) => v === 'individual',
    then: Yup.number().required('Required'),
    otherwise: Yup.number().nullable(),
  }),
  expectOweTaxes: Yup.string().when('type', {
    is: (v: string) => v === 'individual',
    then: Yup.string().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  estimatedPaymentsMade: Yup.string().when('type', {
    is: (v: string) => v === 'individual',
    then: Yup.string().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  estimatedPaymentsAmount: Yup.number().when(
    ['estimatedPaymentsMade', 'type'],
    {
      is: (emp: string, t: string) => t === 'individual' && emp === 'yes',
      then: Yup.number().required('Required'),
      otherwise: Yup.number().nullable(),
    },
  ),
  estimatedTaxLiability: Yup.number().when('type', {
    is: (v: string) => v === 'individual',
    then: Yup.number().required('Required'),
    otherwise: Yup.number().nullable(),
  }),
  stateTaxExtensionRequired: Yup.string().when('type', {
    is: (v: string) => v === 'individual',
    then: Yup.string().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  extraordinaryCircumstances: Yup.string().when('type', {
    is: (v: string) => v === 'individual',
    then: Yup.string().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  circumstancesDescription: Yup.string().when(
    ['extraordinaryCircumstances', 'type'],
    {
      is: (extra: string, t: string) => t === 'individual' && extra === 'yes',
      then: Yup.string().required('Required'),
      otherwise: Yup.string().nullable(),
    },
  ),
  assistanceWithTaxPayments: Yup.string().when('type', {
    is: (v: string) => v === 'individual',
    then: Yup.string().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  extensionFilingMethod: Yup.string().when('type', {
    is: (v: string) => v === 'individual',
    then: Yup.string().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
  contactInformation: Yup.string().when('type', {
    is: (v: string) => v === 'individual',
    then: Yup.string().required('Required'),
    otherwise: Yup.string().nullable(),
  }),
});

export const TaxExtensionFields = [
  // --- Common Instructions Section ---
  {
    type: 'information',
    subLabel: () => (
      <div className="text-sm text-gray-700">
        <span className="text-green font-semibold mr-2">Instructions</span>
        Please provide the necessary information to request a tax filing
        extension. Ensure all personal and business details are accurate,
        including the correct tax year. This information will be used to file
        your extension request.
      </div>
    ),
  },
  // --- Common General Info Section ---
  {
    dependenciesTitle: 'General Info',
    type: 'information',
    dependencies: [
      { label: 'First Name *', name: 'firstName', type: 'text' },
      { label: 'Last Name *', name: 'lastName', type: 'text' },
      {
        label: 'Social Security Number *',
        name: 'ssn',
        type: 'maskedInput',
        mask: '999-99-9999',
      },
      { label: 'Phone Number *', name: 'phoneNumber', type: 'text' },
      { label: 'Email *', name: 'email', type: 'text' },
      // --- Tax Type Field with Conditional Dependencies ---
      {
        name: 'type',
        type: 'radio',
        label: 'Tax Type *',
        options: [
          { label: 'Individual', value: 'individual' },
          { label: 'Business', value: 'business' },
        ],
        dependencies: [
          // Business branch: Show "Select Business" dropdown (populated with client's existing businesses)
          {
            predicate: (v: string) => v === 'business',
            type: 'business',
            label: 'Select Business',
            name: 'business_id',
            options: BusinessTypesOptions, // Replace with client's actual business options if available.
          },
          // New Business Questions (appear only when Business is selected)
          {
            predicate: (v: string) => v === 'business',
            label:
              'Do you have an Employer Identification Number (EIN) for your business?',
            name: 'hasEIN',
            type: 'radio',
            options: [
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ],
            dependencies: [
              {
                label: 'Please provide your EIN',
                name: 'businessEIN',
                type: 'text',
                predicate: (v: string) => v === 'yes',
              },
            ],
          },
          {
            predicate: (v: string) => v === 'business',
            label:
              'Do you have any other tax identification numbers (e.g., TIN, ITIN) associated with your account?',
            name: 'otherTaxIDs',
            type: 'radio',
            options: [
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ],
            dependencies: [
              {
                label: 'Please list them',
                name: 'listOtherTaxIDs',
                type: 'textarea',
                predicate: (v: string) => v === 'yes',
              },
            ],
          },
          {
            predicate: (v: string) => v === 'business',
            label:
              'Are you required to file any separate state or local tax returns that use a different tax identification number?',
            name: 'separateTaxIDRequired',
            type: 'radio',
            options: [
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ],
            dependencies: [
              {
                label: 'Please specify',
                name: 'separateTaxIDSpecification',
                type: 'text',
                predicate: (v: string) => v === 'yes',
              },
            ],
          },
          {
            predicate: (v: string) => v === 'business',
            label:
              'Have you verified that your tax identification numbers are current and correct?',
            name: 'taxIDsVerified',
            type: 'radio',
            options: [
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ],
          },
          {
            predicate: (v: string) => v === 'business',
            label:
              'For business clients: Do you have the necessary documentation (e.g., articles of incorporation, partnership agreements) that may impact your tax filing?',
            name: 'hasBusinessDocumentation',
            type: 'radio',
            options: [
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ],
            dependencies: [
              {
                label:
                  'Would you like assistance in gathering these documents?',
                name: 'assistanceForBusinessDocs',
                type: 'radio',
                options: [
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ],
                predicate: (v: string) => v === 'no',
              },
            ],
          },
          // New Individual Questions (appear only when Individual is selected)
          {
            label: 'Are you married? *',
            name: 'isMarried',
            predicate: (v: string) => v === 'individual',
            type: 'radio',
            options: [
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ],
            dependencies: [
              {
                type: 'text',
                label: 'Spouse’s First Name *',
                name: 'spouseFirstName',
                predicate: (v: string) => v === 'yes',
              },
              {
                type: 'text',
                label: 'Spouse’s Last Name *',
                name: 'spouseLastName',
                predicate: (v: string) => v === 'yes',
              },
              {
                type: 'maskedInput',
                label: 'Spouse’s Social Security Number *',
                name: 'spouseSSN',
                mask: '999-99-9999',
                predicate: (v: string) => v === 'yes',
              },
            ],
          },
          {
            label: 'Do you have dependents? *',
            name: 'haveDependents',
            predicate: (v: string) => v === 'individual',
            type: 'radio',
            options: [
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ],
            dependencies: [
              {
                predicate: (v: string) => v === 'yes',
                label: 'Number of dependents 16 years old or under *',
                name: 'depenendentsCountUnder16',
                type: 'number',
              },
              {
                predicate: (v: string) => v === 'yes',
                label: 'Number of other dependents *',
                name: 'otherdepenendentsCount',
                type: 'number',
              },
            ],
          },
          {
            predicate: (v: string) => v === 'individual',
            label: 'Have you already completed your federal tax return?',
            name: 'completedFederalReturn',
            type: 'radio',
            options: [
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ],
            dependencies: [
              {
                type: 'information',
                predicate: (v: string) => v === 'no',
                subLabel: () => (
                  <div className="text-sm text-yellow-700 italic">
                    An extension may be appropriate.
                  </div>
                ),
              },
            ],
          },
          {
            predicate: (v: string) => v === 'individual',
            label: 'What is your filing status?',
            name: 'filingStatus',
            type: 'select', // Now a dropdown
            options: [
              { label: 'Single', value: 'single' },
              { label: 'Married Filing Jointly', value: 'married_jointly' },
              {
                label: 'Married Filing Separately',
                value: 'married_separately',
              },
              { label: 'Head of Household', value: 'head_of_household' },
              { label: 'Qualifying Widow(er)', value: 'qualifying_widow' },
            ],
          },
          {
            predicate: (v: string) => v === 'individual',
            label:
              'What was your Adjusted Gross Income (AGI) on your prior year’s return?',
            name: 'priorYearAGI',
            type: 'number',
          },
          {
            predicate: (v: string) => v === 'individual',
            label: 'Do you expect to owe taxes for the current year?',
            name: 'expectOweTaxes',
            type: 'radio',
            options: [
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ],
          },
          {
            predicate: (v: string) => v === 'individual',
            label:
              'Have you made any estimated tax payments or had any tax withholdings this year?',
            name: 'estimatedPaymentsMade',
            type: 'radio',
            options: [
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ],
            dependencies: [
              {
                label: 'Total amount paid',
                name: 'estimatedPaymentsAmount',
                type: 'number',
                predicate: (v: string) => v === 'yes',
              },
            ],
          },
          {
            predicate: (v: string) => v === 'individual',
            label:
              'What is your estimated total tax liability for the current year?',
            name: 'estimatedTaxLiability',
            type: 'number',
          },
          {
            predicate: (v: string) => v === 'individual',
            label: 'Do you require a state tax extension as well?',
            name: 'stateTaxExtensionRequired',
            type: 'radio',
            options: [
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ],
            dependencies: [
              {
                label: 'Select the state for your extension',
                name: 'stateTaxExtensionState',
                type: 'state',
                predicate: (v: string) => v === 'yes',
              },
            ],
          },
          {
            predicate: (v: string) => v === 'individual',
            label:
              'Are you facing any extraordinary circumstances affecting your ability to file on time?',
            name: 'extraordinaryCircumstances',
            type: 'radio',
            options: [
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ],
            dependencies: [
              {
                label: 'Please briefly describe',
                name: 'circumstancesDescription',
                type: 'textarea',
                predicate: (v: string) => v === 'yes',
              },
            ],
          },
          {
            predicate: (v: string) => v === 'individual',
            label:
              'Would you like assistance with estimating your tax liability or setting up estimated tax payments to avoid penalties?',
            name: 'assistanceWithTaxPayments',
            type: 'radio',
            options: [
              { label: 'Yes', value: 'yes' },
              { label: 'No', value: 'no' },
            ],
          },
          {
            predicate: (v: string) => v === 'individual',
            label: 'How would you prefer to file your extension?',
            name: 'extensionFilingMethod',
            type: 'radio',
            options: [
              { label: 'Electronically', value: 'electronically' },
              { label: 'By Mail', value: 'by_mail' },
            ],
          },
          {
            predicate: (v: string) => v === 'individual',
            label:
              'Please provide your contact information for follow-up and confirmation',
            name: 'contactInformation',
            type: 'textarea',
          },
        ],
      },
    ],
  },
  // --- Common Address Section ---
  {
    dependenciesTitle: 'Address',
    type: 'information',
    dependencies: [
      { type: 'text', label: 'Address *', name: 'address' },
      { type: 'text', label: 'Apartment #', name: 'apartment' },
      { type: 'text', label: 'City *', name: 'city' },
      { type: 'state', label: 'State *', name: 'state' },
      { type: 'text', label: 'Zip Code *', name: 'zipCode' },
      {
        type: 'radio',
        name: 'liveOutsideOfUs',
        label: 'Do you live outside of the United States? *',
        dependencies: [
          {
            predicate: (v: string) => v === 'yes',
            name: 'otherCountry',
            type: 'country',
            label: 'Country *',
          },
          {
            predicate: (v: string) => v === 'yes',
            name: 'provinceCounty',
            type: 'text',
            label: 'Province/County *',
          },
        ],
      },
    ],
  },
  // --- Common Submit Section ---
  {
    dependenciesTitle: 'Submit Tax Information',
    type: 'information',
    dependencies: [
      {
        name: 'notes',
        type: 'textarea',
        label:
          'Ask for an extension by the April tax filing deadline if you require additional time to file your taxes. You have till October 15th to file without incurring any fines. Ensure that any taxes you owe are paid by the deadline. This is only an extension to file your return.',
      },
      {
        name: 'reachPhoneNumber',
        type: 'text',
        label:
          'At what number can your accountant reach you if there are questions?',
      },
      {
        name: 'whatIsTheBestTimeToReachYouAtThisNumber',
        type: 'radio',
        label: 'What is the best time to reach you at this number?',
        options: ['Anytime', 'Morning', 'Afternoon', 'Evening'].map((o) => ({
          label: o,
          value: o,
        })),
      },
      {
        type: 'information',
        label:
          "To the best of my knowledge the information provided is correct and includes all necessary details for the preparation of this year's income tax returns.",
        dependencies: [
          { name: 'agreement', type: 'checkbox', label: 'I agree' },
        ],
      },
    ],
  },
] as IBaseField<keyof ITaxExtensionForm>[];

export function FormFields({
  updateValue,
  taxExtension,
  type,
}: {
  type: 'business' | 'individual';
  updateValue: (key: keyof ITaxExtensionForm, value: any) => void;
  taxExtension?: Partial<ITaxExtension>;
}) {
  useEffect(() => {
    updateValue('year', nextYear);
  }, [updateValue]);

  return (
    <>
      {TaxExtensionFields.map((input, index) => (
        <Fragment key={index}>{renderInput(input)}</Fragment>
      ))}
    </>
  );
}
